import { api } from './api';

export type PolicyStatus = 'draft' | 'under_review' | 'approved' | 'published' | 'retired';
export type AckState = 'pending' | 'accepted' | 'declined';

export interface PolicyListItem {
  id: string;
  title: string;
  category: string | null;
  status: PolicyStatus;
  version: number;
  reviewDate: string | null;
  needsReview: boolean;
  owner: { id: string; firstName: string; lastName: string } | null;
  publishedAt: string | null;
  compliance: { accepted: number; total: number } | null;
}

export interface PolicyDetail {
  policy: {
    id: string;
    title: string;
    category: string | null;
    status: PolicyStatus;
    version: number;
    bodyHtml: string;
    reviewDate: string | null;
    publishedAt: string | null;
    retiredAt: string | null;
    owner: { id: string; firstName: string; lastName: string } | null;
    versions: {
      id: string;
      version: number;
      publishedAt: string;
      publishedBy: { firstName: string; lastName: string } | null;
    }[];
  };
  currentVersion: { id: string; version: number; publishedAt: string } | null;
  acknowledgements: {
    state: AckState;
    actedAt: string | null;
    user: { id: string; firstName: string; lastName: string };
  }[];
}

export interface MyPendingPolicy {
  versionId: string;
  version: number;
  title: string;
  category: string | null;
  policyId: string;
  publishedAt: string;
}

export interface PolicyVersionRead {
  id: string;
  version: number;
  title: string;
  bodyHtml: string;
  publishedAt: string;
  myState: AckState;
}

export const POLICY_STATUS_LABEL: Record<PolicyStatus, string> = {
  draft: 'Draft',
  under_review: 'Under review',
  approved: 'Approved',
  published: 'Published',
  retired: 'Retired',
};

// ── Admin ────────────────────────────────────────────────────────────────────
export async function fetchPolicies(): Promise<PolicyListItem[]> {
  return (await api.get('/policies')).data.data as PolicyListItem[];
}
export async function fetchPolicy(id: string): Promise<PolicyDetail> {
  return (await api.get(`/policies/${id}`)).data.data as PolicyDetail;
}
export async function createPolicy(input: {
  title: string;
  category?: string;
  bodyHtml?: string;
  reviewDate?: string;
}): Promise<{ id: string; title: string }> {
  return (await api.post('/policies', input)).data.data;
}
export async function updatePolicy(
  id: string,
  input: { title?: string; category?: string | null; bodyHtml?: string; reviewDate?: string | null },
): Promise<void> {
  await api.patch(`/policies/${id}`, input);
}
export async function publishPolicy(id: string): Promise<{ version: number; acknowledgementsRequested: number }> {
  return (await api.post(`/policies/${id}/publish`, {})).data.data;
}
export async function retirePolicy(id: string): Promise<void> {
  await api.post(`/policies/${id}/retire`, {});
}

// ── Member ───────────────────────────────────────────────────────────────────
export async function fetchMyPendingPolicies(): Promise<MyPendingPolicy[]> {
  return (await api.get('/me/policies/pending')).data.data as MyPendingPolicy[];
}
export async function fetchMyPendingCount(): Promise<number> {
  return (await api.get('/me/policies/pending/count')).data.data.count as number;
}
export async function readPolicyVersion(versionId: string): Promise<PolicyVersionRead> {
  return (await api.get(`/me/policies/versions/${versionId}`)).data.data as PolicyVersionRead;
}
export async function acknowledgePolicy(versionId: string, accept: boolean): Promise<void> {
  await api.post(`/me/policies/versions/${versionId}/acknowledge`, { accept });
}
